import React from "react";
import { Box, Typography, Select, MenuItem, Paper, Fade, IconButton, Tooltip } from "@mui/material";
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SettingsAuthModal from './SettingsAuthModal';

/**
 * AccueilBoxe (jugeboxe)
 *
 * - Menu simplifié
 * - Icône Paramètres en haut à droite ouvrant un modal qui demande uniquement le mot de passe.
 *   Après vérification, on navigue vers la page StatsEleves (route : /jugeboxe/stats-eleves).
 */

export default function AccueilBoxe({ onSelectMode, user, isStudentSession = false }) {
  const [mode, setMode] = React.useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();

  // Show teacher-only QR management only for authenticated teacher sessions.
  const isProfVisible = ["admin", "prof", "teacher", "enseignant", "formateur"].includes(user?.role);

  const handleChange = (value) => {
    setMode(value);
    if (value === 'qr-manager') {
      navigate('/apps/juge-boxe/qr-manager');
      return;
    }
    if (value === 1 || value === 2) {
      onSelectMode(value);
    } else {
      onSelectMode(value); // 'qr'
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#08080e",
        background: "linear-gradient(120deg, #1c1c2b 50%, #a51c30 100%)",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {/* Paramètres (top-right) */}
      <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 10 }}>
        <Tooltip title="Paramètres (accès prof)">
          <IconButton onClick={() => setSettingsOpen(true)} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.06)' }}>
            <img src="/assets/parametres.png" alt="parametres" style={{ width: 28, height: 28 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <SettingsAuthModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSuccess={() => {
          setSettingsOpen(false);
          // navigation vers la page StatsEleves (à adapter si ta route diffère)
          navigate('/jugeboxe/stats-eleves');
        }}
      />

      {/* Effet lumière ring */}
      <Box sx={{
        position: "absolute",
        top: "10%",
        left: "50%",
        transform: "translateX(-50%)",
        width: 340,
        height: 170,
        bgcolor: "rgba(255,255,255,0.07)",
        borderRadius: "50%",
        filter: "blur(12px)",
        zIndex: 1,
      }} />

      {/* Logo principal */}
      <Fade in={true} timeout={1200}>
        <img
          src="/assets/judgeboxe_logo.png"
          alt="JUGE BOXE"
          style={{
            width: 280,
            maxWidth: "95%",
            marginTop: 40,
            marginBottom: 20,
            zIndex: 2,
            boxShadow: "0 4px 24px 0 rgba(0,0,0,0.17)",
            borderRadius: "18px",
          }}
        />
      </Fade>

      {/* Slogan */}
      <Typography
        variant="h5"
        sx={{
          color: "#fff",
          textShadow: "0 2px 8px #a51c30",
          fontWeight: 700,
          mb: 4,
          zIndex: 2,
          fontFamily: "Montserrat, sans-serif",
        }}
      >
        Juge ton combat, deviens arbitre !
      </Typography>

      {/* Sélection du mode */}
      <Paper
        elevation={6}
        sx={{
          p: 2,
          px: 3,
          mb: 4,
          borderRadius: 4,
          background: "rgba(255,255,255,0.97)",
          zIndex: 3,
          minWidth: 240,
        }}
      >
        <Select
          value={mode}
          onChange={e => {
            const v = e.target.value;
            handleChange(v);
          }}
          displayEmpty
          fullWidth
          sx={{
            fontSize: 18,
            bgcolor: "#fff",
            borderRadius: 2,
            minWidth: 200,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <MenuItem value="">Mode d’utilisation...</MenuItem>

          <MenuItem value={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" /> Juger un match (1 boxeur)
            </Box>
          </MenuItem>

          <MenuItem value={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="span" sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#e53935', display: 'inline-block' }} />
              <Box component="span" sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#1976d2', display: 'inline-block' }} />
              &nbsp; Juger un match (2 boxeurs)
            </Box>
          </MenuItem>

          {!isStudentSession && (
            <MenuItem value={"qr"}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCode2Icon sx={{ fontSize: 20 }} /> Accès smartphone (QR Code)
              </Box>
            </MenuItem>
          )}

          {isProfVisible && (
            <MenuItem value={"qr-manager"}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon sx={{ fontSize: 20 }} /> Générer QR codes élèves (prof)
              </Box>
            </MenuItem>
          )}
        </Select>
      </Paper>

      {/* Footer */}
      <Typography
        variant="body2"
        sx={{
          color: "#bbb",
          position: "absolute",
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 14,
          zIndex: 2,
        }}
      >
        Powered by MyEPS • 2025
      </Typography>
    </Box>
  );
}